import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { RootStackParamList } from '../types';
import { Button, Input } from '../components';
import { authService } from '../services/auth';
import { Colors, Fonts, Typography, Radii, Shadows } from '../constants';
import { useDialog } from '../contexts';
import { useResponsive } from '../utils/responsive';

type Props = NativeStackScreenProps<RootStackParamList, 'Register'>;

export const RegisterScreen: React.FC<Props> = ({ navigation }) => {
  const { t, i18n } = useTranslation();
  const dialog = useDialog();

  // Form state
  const { isDesktop } = useResponsive();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState<'en' | 'nl'>(
    (i18n.language as 'en' | 'nl') || 'nl'
  );

  // UI state
  const [loading, setLoading] = useState(false);

  // Error state
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  // Email validation helper
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Clear errors when user starts typing
  const handleFirstNameChange = (text: string) => {
    setFirstName(text);
    if (firstNameError) setFirstNameError('');
  };
  
  const handleLastNameChange = (text: string) => {
    setLastName(text);
    if (lastNameError) setLastNameError('');
  };

  const handleEmailChange = (text: string) => {
    setEmail(text);
    if (emailError) setEmailError('');
  };

  const handlePasswordChange = (text: string) => {
    setPassword(text);
    if (passwordError) setPasswordError('');
    // Also clear confirm password error if it was about mismatch
    if (confirmPasswordError && confirmPassword) {
      setConfirmPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (text: string) => {
    setConfirmPassword(text);
    if (confirmPasswordError) setConfirmPasswordError('');
  };

  // Validate on blur (when field loses focus)
  const handleFirstNameBlur = () => {
    if (!firstName.trim()) {
      setFirstNameError(t('common.validation.firstNameRequired'));
    }
  };
  
  const handleLastNameBlur = () => {
    if (!lastName.trim()) {
      setLastNameError(t('common.validation.lastNameRequired'));
    }
  };

  const handleEmailBlur = () => {
    if (!email.trim()) {
      setEmailError(t('common.validation.emailRequired'));
    } else if (!isValidEmail(email.trim())) {
      setEmailError(t('common.validation.emailInvalid'));
    }
  };

  const handlePasswordBlur = () => {
    if (!password) {
      setPasswordError(t('common.validation.passwordRequired'));
    } else if (password.length < 6) {
      setPasswordError(t('common.validation.passwordMinLength'));
    }
  };

  const handleConfirmPasswordBlur = () => {
    if (!confirmPassword) {
      setConfirmPasswordError(t('common.validation.passwordRequired'));
    } else if (password !== confirmPassword) {
      setConfirmPasswordError(t('common.validation.passwordMismatch'));
    }
  };

  const validateFields = () => {
    let isValid = true;

    // Validate first name
    if (!firstName.trim()) {
      setFirstNameError(t('common.validation.firstNameRequired'));
      isValid = false;
    }
    
    // Validate last name
    if (!lastName.trim()) {
      setLastNameError(t('common.validation.lastNameRequired'));
      isValid = false;
    }

    // Validate email
    if (!email.trim()) {
      setEmailError(t('common.validation.emailRequired'));
      isValid = false;
    } else if (!isValidEmail(email.trim())) {
      setEmailError(t('common.validation.emailInvalid'));
      isValid = false;
    }

    // Validate password
    if (!password) {
      setPasswordError(t('common.validation.passwordRequired'));
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError(t('common.validation.passwordMinLength'));
      isValid = false;
    }

    // Validate confirm password
    if (!confirmPassword) {
      setConfirmPasswordError(t('common.validation.passwordRequired'));
      isValid = false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError(t('common.validation.passwordMismatch'));
      isValid = false;
    }

    return isValid;
  };

  const handleRegister = async () => {
    // Clear previous errors
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');

    // Validate fields
    if (!validateFields()) {
      return;
    }

    setLoading(true);
    try {
      await authService.signUp(
        email.trim(),
        password,
        firstName.trim(),
        lastName.trim(),
        preferredLanguage
      );
      // Navigation will be handled by auth state change
      dialog.alert(
        t('screens.register.success'),
        t('screens.register.successMessage'),
        'success'
      );
    } catch (error: any) {
      // Handle specific registration errors
      const errorMessage = error.message || '';

      if (errorMessage.includes('User already registered')) {
        setEmailError(t('common.validation.emailAlreadyExists'));
      } else if (errorMessage.includes('Invalid email')) {
        setEmailError(t('common.validation.emailInvalid'));
      } else if (errorMessage.includes('Password should be at least 6 characters')) {
        setPasswordError(t('common.validation.passwordMinLength'));
      } else {
        dialog.alert(t('screens.register.registrationFailed'), errorMessage || t('common.errors.unknown'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.formWrapper}>
          <View style={styles.header}>
            <Text style={[styles.title, !isDesktop && styles.titleMobile]}>
              {t('screens.register.title')}
            </Text>
            <Text style={styles.subtitle}>{t('screens.register.subtitle')}</Text>
          </View>

          <View style={styles.form}>
            {/* Language Toggle */}
            <View style={styles.languageSection}>
              <Text style={styles.languageLabel}>
                {t('screens.register.preferredLanguage')}
              </Text>
              <View style={styles.languageToggle}>
                <TouchableOpacity
                  style={[
                    styles.languageButton,
                    preferredLanguage === 'en' && styles.languageButtonActive,
                  ]}
                  onPress={() => {
                    setPreferredLanguage('en');
                    i18n.changeLanguage('en');
                  }}
                >
                  <Text
                    style={[
                      styles.languageButtonText,
                      preferredLanguage === 'en' && styles.languageButtonTextActive,
                    ]}
                  >
                    English
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.languageButton,
                    preferredLanguage === 'nl' && styles.languageButtonActive,
                  ]}
                  onPress={() => {
                    setPreferredLanguage('nl');
                    i18n.changeLanguage('nl');
                  }}
                >
                  <Text
                    style={[
                      styles.languageButtonText,
                      preferredLanguage === 'nl' && styles.languageButtonTextActive,
                    ]}
                  >
                    Nederlands
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Name Fields */}
            <Input
              label={t('screens.register.firstName')}
              value={firstName}
              onChangeText={handleFirstNameChange}
              onBlur={handleFirstNameBlur}
              placeholder={t('screens.register.firstNamePlaceholder')}
              autoCapitalize="words"
              autoComplete="name-given"
              error={firstNameError}
            />
            <Input
              label={t('screens.register.lastName')}
              value={lastName}
              onChangeText={handleLastNameChange}
              onBlur={handleLastNameBlur}
              placeholder={t('screens.register.lastNamePlaceholder')}
              autoCapitalize="words"
              autoComplete="name-family"
              error={lastNameError}
            />

            <Input
              label={t('screens.register.email')}
              icon="mail-outline"
              value={email}
              onChangeText={handleEmailChange}
              onBlur={handleEmailBlur}
              placeholder={t('screens.register.emailPlaceholder')}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              error={emailError}
            />
            <Input
              label={t('screens.register.password')}
              icon="lock-closed-outline"
              value={password}
              onChangeText={handlePasswordChange}
              onBlur={handlePasswordBlur}
              placeholder={t('screens.register.passwordPlaceholder')}
              secureTextEntry
              autoComplete="password-new"
              error={passwordError}
            />
            <Input
              label={t('screens.register.confirmPassword')}
              icon="lock-closed-outline"
              value={confirmPassword}
              onChangeText={handleConfirmPasswordChange}
              onBlur={handleConfirmPasswordBlur}
              placeholder={t('screens.register.confirmPasswordPlaceholder')}
              secureTextEntry
              autoComplete="password-new"
              error={confirmPasswordError}
            />

            <View style={[styles.buttonContainer, isDesktop && styles.buttonContainerDesktop]}>
              <Button
                title={t('common.buttons.register')}
                onPress={handleRegister}
                loading={loading}
                style={styles.registerButton}
              />
              <Button
                title={t('common.buttons.backToLogin')}
                onPress={() => navigation.navigate('Login')}
                variant="outline"
                style={styles.secondaryButton}
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 24,
    alignItems: 'center',
  },
  formWrapper: {
    width: '100%',
    ...(Platform.OS === 'web' && {
      maxWidth: 500,
    }),
  },
  header: {
    alignItems: 'center',
    marginBottom: 36,
  },
  title: {
    ...Typography.title,
    color: Colors.textDark,
    textAlign: 'center',
    marginBottom: 6,
  },
  titleMobile: {
    ...Typography.displayMobile,
  },
  subtitle: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    lineHeight: 22,
    color: Colors.textMedium,
    textAlign: 'center',
    maxWidth: 380,
  },
  form: {
    width: '100%',
  },
  buttonContainer: {
    width: '100%',
    gap: 12,
    marginTop: 8,
  },
  buttonContainerDesktop: {
    flexDirection: 'row',
    gap: 16,
  },
  registerButton: {
    flex: 1,
  },
  secondaryButton: {
    flex: 1,
  },
  languageSection: {
    marginBottom: 24,
  },
  languageLabel: {
    fontFamily: Fonts.semiBold,
    fontSize: 14,
    color: Colors.textDark,
    marginBottom: 8,
  },
  languageToggle: {
    flexDirection: 'row',
    gap: 4,
    padding: 4,
    borderRadius: Radii.control,
    backgroundColor: Colors.background,
    borderWidth: 1,
    borderColor: Colors.borderLight,
  },
  languageButton: {
    flex: 1,
    height: 40,
    borderRadius: Radii.thumb,
    alignItems: 'center',
    justifyContent: 'center',
  },
  languageButtonActive: {
    backgroundColor: Colors.white,
    ...Shadows.card,
  },
  languageButtonText: {
    fontFamily: Fonts.semiBold,
    fontSize: 14,
    color: Colors.textMedium,
  },
  languageButtonTextActive: {
    color: Colors.textDark,
  },
});
